import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { TutorService } from '../tutor.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { Router } from '@angular/router';
import { CONSTANTS } from '../../../../core/constants';
import { CommonService } from '../../../../core/common.service';
import { GlobalFunctions } from '../../../../core/global-functions';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ExperienceFormComponent } from '../../../../shared/modals/tutor/experience-form/experience-form.component';

export const dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startYear = control.get('start_year')?.value;
  const startMonth = control.get('start_month')?.value;
  const endYear = control.get('end_year')?.value;
  const endMonth = control.get('end_month')?.value;
  const currentlyWorking = control.get('is_currently_working')?.value;

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (!currentlyWorking && startYear && startMonth && endYear && endMonth) {
    const startIdx = months.indexOf(startMonth);
    const endIdx = months.indexOf(endMonth);
    
    if (startIdx !== -1 && endIdx !== -1) {
      const startDate = new Date(startYear, startIdx);
      const endDate = new Date(endYear, endIdx);
      
      if (startDate > endDate) {
        return { dateRange: true };
      }
    }
  }
  return null;
};

@Component({
  selector: 'app-experience',
  standalone: true,
  imports: [CommonModule, ImageModule, ReactiveFormsModule, FormsModule, MultiSelectModule, DropdownModule, CheckboxModule, TooltipModule, DialogModule, ToastModule, ExperienceFormComponent],
  templateUrl: './experience.component.html',
  styleUrl: './experience.component.scss'
})
export class ExperienceComponent implements OnInit {

  constants: any = CONSTANTS;

  experienceForm: any = FormGroup;
  profileDetails: any;

  isBtnLoading: boolean = false;
  displaySkipDialog: boolean = false;
  editingIndex: number | null = null;
  
  showExperienceForm: boolean = false;
  selectedExperienceItem: any = null;

  yearList: any = [];
  subjects: any = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _tutorServices: TutorService,
    public _globalFunctions: GlobalFunctions,
    private messageService: MessageService,
    private _router: Router,
    private _commonService: CommonService
  ) {
    const startYear = 1990;
    const currentYear = new Date().getFullYear();
    for (let year = startYear; year <= currentYear; year++) {
      this.yearList.push({ name: year.toString(), value: year });
    };
  }

  ngOnInit(): void {
    this.prepareExperienceFormObj();
    this.getUserDetails();
    this.getSubjectList();
  }

  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data); // Update local storage
          this.prepareExperienceFormObj(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }

  getSubjectList() {
    this._commonService.getSubjects().subscribe((result: any) => {
      if (result && result.success) {
        const rawSubjects = result.data?.subjects || result.data || [];
        this.subjects = rawSubjects.map((s: any) => ({
          ...s,
          id: s.id || s._id
        }));
      } else {
        this._globalFunctions.successErrorHandling(result?.message, this, true);
      }
    }, (error: any) => {
      this._globalFunctions.errorHanding(error, this, true);
    })
  }

  get experience(): FormArray {
    return this.experienceForm.get('experience') as FormArray;
  }

  sortExperiences() {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    this.experience.controls.sort((a: any, b: any) => {
      const yearA = parseInt(a.get('start_year')?.value || 0);
      const yearB = parseInt(b.get('start_year')?.value || 0);
      if (yearB !== yearA) return yearB - yearA;
      
      const monthA = months.indexOf(a.get('start_month')?.value || '');
      const monthB = months.indexOf(b.get('start_month')?.value || '');
      return monthB - monthA;
    });
  }

  createExperienceListForm(tempObj: any = {}) {
    const formGroup = this._formBuilder.group({
      id: [tempObj?.id || ''],
      type: [tempObj?.type || '', [Validators.required]],
      institution_name: [tempObj?.institution_name || '', [Validators.required]],
      location: [tempObj?.location || '', [Validators.required]],
      subjects: [tempObj?.subjects?.map((s: any) => s.id || s) || tempObj?.subjects || [], [Validators.required, Validators.maxLength(5)]],
      start_month: [tempObj?.start_month || '', [Validators.required]],
      start_year: [tempObj?.start_year || '', [Validators.required]],
      end_month: [tempObj?.end_month || ''],
      end_year: [tempObj?.end_year || ''],
      teaching_summary: [tempObj?.teaching_summary || '', [Validators.required]],
      document: [tempObj?.file?.document || tempObj?.document || ''],
      is_currently_working: [tempObj?.is_currently_working || false],
    }, { validators: dateRangeValidator });

    return formGroup;
  }

  openDialog(index: number | null = null) {
    this.editingIndex = index;
    if (index !== null) {
      this.selectedExperienceItem = this.experience.at(index).value;
    } else {
      this.selectedExperienceItem = null;
    }
    this.showExperienceForm = true;
  }

  closeDialog() {
    this.showExperienceForm = false;
    this.editingIndex = null;
  }

  onExperienceSaved(savedItem: any) {
    if (this.editingIndex === null) {
      this.experience.push(this.createExperienceListForm(savedItem));
    } else {
      this.experience.at(this.editingIndex).patchValue(savedItem);
    }
    this.sortExperiences();
    this.closeDialog();
  }

  removeExperienceItem(index: number) {
    const id = this.experience.at(index).get('id')?.value;
    if (!id) {
      this.experience.removeAt(index);
      return;
    }

    this._tutorServices.deleteExperience(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          this.experience.removeAt(index);
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message });
        }
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Something went wrong' });
      }
    });
  }

  onSkip() {
    this.displaySkipDialog = true;
  }

  confirmSkip() {
    this.displaySkipDialog = false;
    this._tutorServices.skipStep('experience').subscribe({
      next: (res: any) => {
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message || 'Step skipped successfully' });
          this._router.navigate(['/tutor-signup/' + this.constants.tutorAvailibilityUrl]);
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to skip step' });
        }
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Something went wrong' });
      }
    });
  }

  getSubjectName(id: any, subjects: any[]): string {
    const subject = subjects?.find(s => (s.id || s._id) === id);
    return subject ? subject.name : '';
  }

  goBack() {
    this._router.navigate(['/tutor-signup/' + this.constants.tutorEducationUrl]);
  }

  updateExperience() {
    if (this.experienceForm.get('do_you_have_experience').value && this.experience.length === 0) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please add at least one experience record' });
      return;
    }

    this._router.navigate(['/tutor-signup/' + this.constants.tutorAvailibilityUrl]);
  }

  prepareExperienceFormObj(tempObj: any = {}) {
    let experienceControls: any = [];
    if (tempObj?.experiences && tempObj?.experiences.length > 0) {
      tempObj?.experiences.forEach((element: any) => {
        experienceControls.push(this.createExperienceListForm(element));
      });
    }

    this.experienceForm = this._formBuilder.group({
      do_you_have_experience: [tempObj?.do_you_have_experience || false],
      experience: this._formBuilder.array(experienceControls),
    });

    this.sortExperiences();
  }

}